import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pagination } from 'antd';
import { FiCalendar, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import useGet from '../customHooks/useGet';
import PeopleVisitCard from './PeopleVisitCard';

const PAGE_SIZE = 12;
const SOURCES = [
  ['direct', 'Direct'], ['google', 'Google'], ['organic_search', 'Organic Search'],
  ['paid_search', 'Paid Search'], ['social', 'Social'], ['referral', 'Referral'],
  ['campaign', 'Campaign'], ['unknown', 'Unknown'],
];

const formatDateTime = (value, withSeconds = false) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: withSeconds ? 'medium' : 'short' });
};

const humanize = (value) => {
  if (!value) return '—';
  return String(value).split('/').filter(Boolean).pop().replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const listFrom = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.sessions)) return data.sessions;
  return [];
};

const paginationFrom = (response) => response?.pagination || response?.data?.pagination || response?.data?.meta || {};
const displaySource = (value) => SOURCES.find(([key]) => key === value)?.[1] || humanize(value) || 'Unknown';
const shortId = (value) => !value || value.length < 18 ? value || '—' : `${value.slice(0, 12)}…${value.slice(-6)}`;
const getReferrer = (session) => session?.initial_referrer || session?.referrer || session?.referrer_url || '';

const ReferrerLink = ({ session }) => {
  const referrer = getReferrer(session);
  if (!referrer) return <span>{session?.source === 'direct' ? 'Direct' : '—'}</span>;

  const href = /^https?:\/\//i.test(referrer) ? referrer : `https://${referrer}`;
  return <a href={href} target='_blank' rel='noreferrer' title={referrer} className='block max-w-[180px] truncate text-xs text-[#401A89] hover:underline'>{referrer}</a>;
};

const errorMessage = (error, fallback) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'You do not have permission to view visitor sessions.';
  if (status === 404) return 'Visitor sessions are not available from the configured API.';
  if (status === 422) return error?.response?.data?.message || 'Please check the selected filters.';
  return fallback;
};

const Info = ({ label, value, wide = false }) => (
  <div className={wide ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
    <div className='text-[0.65rem] uppercase tracking-[0.16em] text-[#7D8CA7]'>{label}</div>
    <div className='mt-1 break-words text-sm text-[#292634]'>{value || '—'}</div>
  </div>
);

const JourneyDetails = ({ open, session, loading, error, onClose }) => {
  const timeline = Array.isArray(session?.timeline) ? session.timeline : (Array.isArray(session?.journey) ? session.journey : []);
  return (
    <Modal open={open} title='Visitor Journey' footer={null} onCancel={onClose} closeIcon={<FiX />} width={760}>
      {loading ? <div className='animate-pulse space-y-3'><div className='h-20 rounded-xl bg-[#F1EFF6]' /><div className='h-64 rounded-xl bg-[#F1EFF6]' /></div> : error ? (
        <div className='rounded-xl border border-[#efb6bd] bg-[#fff8f8] p-5 text-sm text-[#a72b3b]'>{error}</div>
      ) : !session ? <div className='py-10 text-center text-[#7D8CA7]'>No journey details found.</div> : (
        <div className='space-y-5'>
          <div className='grid grid-cols-2 gap-4 rounded-2xl bg-[#F7F5FA] p-4 sm:grid-cols-4'>
            <Info label='Session ID' value={session.tracking_session_id || session.session_id} wide />
            <Info label='Source' value={displaySource(session.source)} />
            <Info label='Landing Page' value={session.landing_page || humanize(session.landing_slug)} />
            <Info label='First Visit' value={formatDateTime(session.first_seen_at)} />
            <Info label='Last Visit' value={formatDateTime(session.last_seen_at)} />
            <Info label='Pages Viewed' value={session.total_page_views ?? timeline.filter((event) => event?.type === 'page_view').length} />
            <Info label='Conversion' value={<span className={session.converted ? 'statusBadge statusBadge--completed' : 'statusBadge'}>{session.converted ? 'Converted' : 'Not Converted'}</span>} />
            <Info label='Converted At' value={formatDateTime(session.converted_at)} />
          </div>
          <section className='rounded-2xl border border-[#E3DFEA] p-4'>
            <h3 className='mb-4 text-base font-semibold text-[#292634]'>Acquisition</h3>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <Info label='Initial Referrer' value={<ReferrerLink session={session} />} wide />
              <Info label='UTM Source' value={session.utm_source} />
              <Info label='UTM Medium' value={session.utm_medium} />
              <Info label='UTM Campaign' value={session.utm_campaign} />
              <Info label='UTM Term' value={session.utm_term} />
              <Info label='UTM Content' value={session.utm_content} />
            </div>
          </section>
          <section>
            <h3 className='mb-4 text-base font-semibold text-[#292634]'>Visitor Journey</h3>
            {timeline.length === 0 ? <div className='rounded-xl border border-dashed border-[#D4DEF1] p-6 text-center text-sm text-[#7D8CA7]'>No timeline events were returned for this session.</div> : (
              <div className='relative ml-2 border-l-2 border-[#DDD7EA] pl-6'>
                {timeline.map((event, index) => {
                  const isQuote = event?.type === 'quote_submit';
                  return <div key={`${event?.timestamp || index}-${index}`} className='relative pb-5 last:pb-0'>
                    <span className={`absolute -left-[33px] top-1 h-3 w-3 rounded-full border-2 border-white ${isQuote ? 'bg-[#167452]' : 'bg-[#401A89]'}`} />
                    <div className={`rounded-xl border p-3 ${isQuote ? 'border-[#bde5d2] bg-[#f1fbf5]' : 'border-[#E3DFEA] bg-white'}`}>
                      <div className='flex flex-wrap items-center justify-between gap-2'><strong className='text-sm text-[#292634]'>{isQuote ? 'Quote Submitted' : (event?.title || humanize(event?.slug) || 'Page View')}</strong><span className='text-xs text-[#7D8CA7]'>{formatDateTime(event?.timestamp || event?.created_at, true)}</span></div>
                      {!isQuote && <div className='mt-1 break-all text-xs text-[#7D8CA7]'>{event?.slug ? `/${String(event.slug).replace(/^\//, '')}` : '—'}</div>}
                    </div>
                  </div>;
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
};

const PeopleVisits = () => {
  const [tab, setTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [aggregate, setAggregate] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '', source: '', converted: '', date_from: '', date_to: '' });
  const [pagination, setPagination] = useState({});
  const [resget, apiMethodGet] = useGet();
  const [aggregateRes, aggregateGet] = useGet();
  const [detailRes, detailGet] = useGet();
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (tab !== 'sessions') return;
    const params = new URLSearchParams({ page: String(currentPage), per_page: String(PAGE_SIZE) });
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
    apiMethodGet(`people-visits/sessions?${params.toString()}`);
  }, [tab, currentPage, filters]);

  useEffect(() => { if (tab === 'aggregate') aggregateGet('people-visits?per_page=12'); }, [tab]);
  useEffect(() => {
    if (resget.data && !resget.error) { setSessions(listFrom(resget.data)); setPagination(paginationFrom(resget.data)); }
  }, [resget.data, resget.error]);
  useEffect(() => { if (aggregateRes.data && !aggregateRes.error) setAggregate(listFrom(aggregateRes.data)); }, [aggregateRes.data, aggregateRes.error]);
  useEffect(() => { if (resget.error) toast.error(errorMessage(resget.error, 'Unable to load visitor sessions.')); }, [resget.error]);
  useEffect(() => { if (aggregateRes.error) toast.error(errorMessage(aggregateRes.error, 'Unable to load page statistics.')); }, [aggregateRes.error]);
  useEffect(() => {
    if (detailRes.data && !detailRes.error) setSelectedSession(detailRes.data?.data || detailRes.data);
    if (detailRes.error) toast.error(errorMessage(detailRes.error, 'Unable to load this visitor journey.'));
  }, [detailRes.data, detailRes.error]);

  const openJourney = (session) => {
    const id = session?.tracking_session_id || session?.session_id;
    if (!id) { toast.error('This session does not contain a tracking session ID.'); return; }
    setSelectedSession(null);
    setDetailOpen(true);
    detailGet(`people-visits/sessions/${encodeURIComponent(id)}`);
  };

  const applySearch = (event) => { event.preventDefault(); setCurrentPage(1); setFilters((previous) => ({ ...previous, search: searchInput.trim() })); };
  const resetFilters = () => { setSearchInput(''); setCurrentPage(1); setFilters({ search: '', source: '', converted: '', date_from: '', date_to: '' }); };
  const pageTotal = pagination?.total ?? pagination?.total_items ?? sessions.length;
  const loading = resget.isLoading;
  const rows = useMemo(() => sessions.map((item, index) => ({ ...item, _key: item.tracking_session_id || item.session_id || index })), [sessions]);

  return <div className='TeamPage pr-10 max-lg:pr-6'>
    <div className='mb-5 flex flex-wrap items-end justify-between gap-3'>
      <div><p className='pageEyebrow'>Analytics</p><h1 className='bookingSectionh'>People Visits</h1><p className='text-sm text-[#7D8CA7]'>Understand visitor acquisition, journeys, and conversions.</p></div>
      <div className='flex rounded-lg border border-[#D4DEF1] bg-white p-1'>
        <button type='button' onClick={() => setTab('sessions')} className={`px-4 py-2 text-sm ${tab === 'sessions' ? 'rounded-md bg-[#401A89] text-white' : 'text-[#7D8CA7]'}`}>Visitor Journeys</button>
        <button type='button' onClick={() => setTab('aggregate')} className={`px-4 py-2 text-sm ${tab === 'aggregate' ? 'rounded-md bg-[#401A89] text-white' : 'text-[#7D8CA7]'}`}>Page Statistics</button>
      </div>
    </div>
    {tab === 'sessions' ? <>
      <form onSubmit={applySearch} className='mb-4 grid grid-cols-1 gap-3 rounded-2xl border border-[#D4DEF1] bg-white p-4 sm:grid-cols-2 lg:grid-cols-6'>
        <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder='Search session, page, referrer' className='lg:col-span-2' />
        <select value={filters.source} onChange={(event) => { setCurrentPage(1); setFilters((previous) => ({ ...previous, source: event.target.value })); }}><option value=''>All sources</option>{SOURCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        <select value={filters.converted} onChange={(event) => { setCurrentPage(1); setFilters((previous) => ({ ...previous, converted: event.target.value })); }}><option value=''>All conversions</option><option value='1'>Converted</option><option value='0'>Not Converted</option></select>
        <label className='relative'><FiCalendar className='pointer-events-none absolute right-3 top-3.5 text-[#7D8CA7]' /><input type='date' value={filters.date_from} onChange={(event) => { setCurrentPage(1); setFilters((previous) => ({ ...previous, date_from: event.target.value })); }} aria-label='Date from' /></label>
        <label className='relative'><FiCalendar className='pointer-events-none absolute right-3 top-3.5 text-[#7D8CA7]' /><input type='date' value={filters.date_to} onChange={(event) => { setCurrentPage(1); setFilters((previous) => ({ ...previous, date_to: event.target.value })); }} aria-label='Date to' /></label>
        <div className='flex gap-2 sm:col-span-2 lg:col-span-6'><button type='submit' className='primaryButton px-5'>Search</button><button type='button' onClick={resetFilters} className='rounded-lg border border-[#D4DEF1] px-5 text-sm text-[#514b5e]'>Reset</button></div>
      </form>
      <div className='overflow-x-auto rounded-2xl bg-white'><table className='min-w-[1050px]'><thead><tr><th>Session</th><th>Source</th><th>Referrer</th><th>Landing Page</th><th>Pages</th><th>First Visit</th><th>Last Visit</th><th>Status</th><th>Converted At</th><th>Action</th></tr></thead><tbody>
        {loading ? Array.from({ length: 6 }, (_, index) => <tr key={index}><td colSpan='10'><div className='h-8 animate-pulse rounded bg-[#F1EFF6]' /></td></tr>) : rows.length === 0 ? <tr><td colSpan='10'><div className='py-12 text-center text-sm text-[#7D8CA7]'>No visitor sessions found.</div></td></tr> : rows.map((item) => <tr key={item._key}>
          <td title={item.tracking_session_id || item.session_id}><span className='block max-w-[150px] truncate font-mono text-xs'>{shortId(item.tracking_session_id || item.session_id)}</span></td><td><span className='statusBadge'>{displaySource(item.source)}</span></td><td><ReferrerLink session={item} /></td><td><span className='block max-w-[150px] truncate' title={item.landing_page || item.landing_slug}>{item.landing_page || humanize(item.landing_slug)}</span></td><td>{item.total_page_views ?? '—'}</td><td className='whitespace-nowrap text-xs'>{formatDateTime(item.first_seen_at)}</td><td className='whitespace-nowrap text-xs'>{formatDateTime(item.last_seen_at)}</td><td><span className={`statusBadge ${item.converted ? 'statusBadge--completed' : ''}`}>{item.converted ? 'Converted' : 'Not Converted'}</span></td><td className='whitespace-nowrap text-xs'>{formatDateTime(item.converted_at)}</td><td><button type='button' onClick={() => openJourney(item)} className='whitespace-nowrap text-sm font-semibold text-[#401A89] hover:underline'>View Journey</button></td>
        </tr>)}
      </tbody></table></div>
      <div className='mt-4'><Pagination current={currentPage} onChange={setCurrentPage} total={pageTotal} pageSize={pagination?.per_page || PAGE_SIZE} showSizeChanger={false} /></div>
    </> : <div className='NewsPageGrid mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>{aggregateRes.isLoading ? Array.from({ length: 8 }, (_, index) => <div key={index} className='h-32 animate-pulse rounded-2xl bg-white' />) : aggregate.length ? aggregate.map((item, index) => <PeopleVisitCard key={item?.slug || item?.visit_datetime || index} data={item} />) : <div className='col-span-full py-12 text-center text-sm text-[#7D8CA7]'>No page statistics found.</div>}</div>}
    <JourneyDetails open={detailOpen} session={selectedSession} loading={detailRes.isLoading} error={detailRes.error && errorMessage(detailRes.error, 'Unable to load this visitor journey.')} onClose={() => setDetailOpen(false)} />
  </div>;
};

export default PeopleVisits;
